import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { AvailableLangs, TranslocoService } from '@jsverse/transloco';
import { take } from 'rxjs';

@Component({
  selector: 'languages',
  templateUrl: './languages.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'languages',
  imports: [MatButtonModule, MatMenuModule, NgTemplateOutlet],
})
export class LanguagesComponent implements OnInit, OnDestroy {
  availableLangs: AvailableLangs;
  activeLang: string;
  flagCodes: any;

  /**
   * Constructor
   */
  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _fuseNavigationService: FuseNavigationService,
    private _translocoService: TranslocoService,
    private _matPaginatorIntl: MatPaginatorIntl
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Get the available languages from transloco
    this.availableLangs = this._translocoService.getAvailableLangs();

    // Subscribe to language changes
    this._translocoService.langChanges$.subscribe((activeLang) => {
      // Get the active lang
      this.activeLang = activeLang;

      // Update the navigation
      this._matPaginatorIntl.getRangeLabel = this._getRangeLabel.bind(this);
      this._updatePaginator(activeLang);

      // Update the navigation
      this._updateNavigation(activeLang);
    });

    // Set the country iso codes for languages for flags
    this.flagCodes = {
      en: 'us',
      'zh-hant': 'tw',
    };
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Set the active lang
   *
   * @param lang
   */
  setActiveLang(lang: string): void {
    // Set the active lang
    this._translocoService.setActiveLang(lang);
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  private _updatePaginator(lang: string): void {
    this._translocoService
      .selectTranslate('paginator.first-page')
      .pipe(take(1))
      .subscribe((res: string) => {
        this._matPaginatorIntl.firstPageLabel = this._translocoService.translate('paginator.first-page');
        this._matPaginatorIntl.lastPageLabel = this._translocoService.translate('paginator.last-page');
        this._matPaginatorIntl.itemsPerPageLabel = this._translocoService.translate('paginator.per-page');
        this._matPaginatorIntl.nextPageLabel = this._translocoService.translate('paginator.next-page');
        this._matPaginatorIntl.previousPageLabel = this._translocoService.translate('paginator.previous-page');
        this._matPaginatorIntl.changes.next();
      });
  }

  private _getRangeLabel(page: number, pageSize: number, length: number): string {
    if (length === 0 || pageSize === 0) {
      return this._translocoService.translate('paginator.empty');
    }

    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;

    return this._translocoService.translate('paginator.range-label', {
      startIndex: startIndex + 1,
      endIndex: endIndex,
      total: length,
    });
  }

  /**
   * Update the navigation
   *
   * @param lang
   * @private
   */
  private _updateNavigation(lang: string): void {
    // For the demonstration purposes, we will only update the Dashboard names
    // from the navigation but you can do a full swap and change the entire
    // navigation data.
    //
    // You can import the data from a file or request it from your backend,
    // it's up to you.

    // Get the component -> navigation data -> item
    const navComponent = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');

    // Return if the navigation component does not exist
    if (!navComponent) {
      return null;
    }

    // Get the flat navigation data
    const navigation = navComponent.navigation;

    const navItems = [];
    this._translateMenu(navigation, navItems);

    // Get the Project dashboard item and update its title
    for (const i of navItems) {
      const menuItem = this._fuseNavigationService.getItem(i, navigation);
      if (menuItem) {
        this._translocoService
          .selectTranslate(`side-menu.${i}`)
          .pipe(take(1))
          .subscribe((translation) => {
            // Set the title
            menuItem.title = translation;

            // Refresh the navigation component
            navComponent.refresh();
          });
      }
    }
  }

  private _translateMenu(sideMenu: Array<any>, navItems: Array<string>): void {
    sideMenu.forEach((i) => {
      if (i.id) {
        navItems.push(i.id);
      }
      if (i.children) {
        this._translateMenu(i.children, navItems);
      }
    });
  }
}
